import React from "react";
import { ArrowsClockwise, TrendUp, TrendDown, Minus, Sparkle, ChatCircle, Question } from "@phosphor-icons/react";

const TREND_VISUAL = {
  Up: { icon: <TrendUp size={14} weight="fill" />, tone: "text-emerald-700 bg-emerald-100 border-emerald-200" },
  Down: { icon: <TrendDown size={14} weight="fill" />, tone: "text-red-700 bg-red-100 border-red-200" },
  Flat: { icon: <Minus size={14} weight="fill" />, tone: "text-slate-700 bg-slate-100 border-slate-200" },
};

export default function ExecutiveBrief({ brief, audience = "Partner", onRefresh, refreshing, cached }) {
  if (!brief) return null;
  const v = TREND_VISUAL[brief.trend] || TREND_VISUAL.Flat;
  return (
    <div
      className="rounded-lg border border-[#0B2A5B]/30 bg-gradient-to-br from-white via-blue-50/50 to-white p-6 relative overflow-hidden"
      data-testid="executive-brief-panel"
    >
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#0B2A5B] via-[#1E40AF] to-[#A8B86C] ai-shimmer-bar" />
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#0B2A5B] text-white flex items-center justify-center shrink-0">
          <Sparkle size={22} weight="fill" className="ai-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Geist" }}>
              AI Executive Brief
            </h3>
            <span className="text-[10px] uppercase tracking-wider text-[#0B2A5B] bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded font-semibold">
              For {audience}
            </span>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold flex items-center gap-1 border ${v.tone}`}>
              {v.icon} {brief.trend || "Flat"}
            </span>
            {cached && (
              <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded">
                Cached
              </span>
            )}
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                className="ml-auto p-1 rounded hover:bg-blue-100 text-[#0B2A5B] disabled:opacity-50"
                title="Re-run AI brief"
                data-testid="executive-brief-refresh-btn"
              >
                <ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} />
              </button>
            )}
          </div>
          <p className="text-sm text-slate-800 mt-3 leading-relaxed font-medium" data-testid="executive-brief-summary">
            {brief.summary || "—"}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <BriefList title="What's Improving" tone="emerald" items={brief.improving} testId="improving" icon={<TrendUp size={12} weight="fill" />} />
            <BriefList title="What's Slipping" tone="amber" items={brief.slipping} testId="slipping" icon={<TrendDown size={12} weight="fill" />} />
            <BriefList title="Discuss Next" tone="blue" items={brief.discuss} testId="discuss" icon={<Question size={12} weight="fill" />} />
          </div>
        </div>
      </div>
    </div>
  );
}

const TONES = {
  emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-900",
  amber: "border-amber-200 bg-amber-50/60 text-amber-900",
  blue: "border-blue-200 bg-blue-50/60 text-blue-900",
};

function BriefList({ title, tone, items, testId, icon }) {
  if (!items || !items.length) return null;
  return (
    <div className={`rounded-md border p-3 ${TONES[tone] || TONES.blue}`} data-testid={`exec-brief-${testId}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 mb-1.5">
        {icon} {title}
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={`${testId}-${i}-${(it || "").slice(0, 20)}`} className="text-xs leading-snug flex gap-1.5">
            <ChatCircle size={10} weight="fill" className="mt-1 shrink-0 opacity-60" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
